import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { Check, Loader2 } from 'lucide-react';
import { Badge } from '@bliks-io/ui';
import type { MembershipSummary } from '@bliks-io/contracts';
import { AuthPageLayout } from '@/components/auth/AuthPageLayout';
import { useMemberships } from '@/api/queries/useMemberships';
import { useSwitchWorkspace } from '@/lib/auth/useSwitchWorkspace';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { signedInDestination } from '@/lib/auth/signedInDestination';
import { useAuthStore } from '@/stores/auth-store';

/**
 * Post-login workspace picker for identities with more than one membership.
 * The session already holds the default workspace — picking it just proceeds;
 * picking another one re-scopes the session via switch-tenant (which also
 * accepts a pending invite).
 */
export function SelectWorkspacePage() {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.login');
  const navigate = useNavigate();
  const { memberships, isLoading } = useMemberships();
  const { switchWorkspace, isSwitching } = useSwitchWorkspace();
  const { tenant, tenantType } = useAuthStore();

  const handleSelect = (membership: MembershipSummary) => {
    if (membership.tenantId === tenant?._id) {
      // Already scoped to this workspace — no re-mint needed.
      navigate(signedInDestination({ tenant, tenantType: tenantType ?? 'customer' }));
      return;
    }
    switchWorkspace(membership.tenantId);
  };

  if (isLoading) {
    return (
      <AuthPageLayout description={t('selectWorkspaceDescription')}>
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout description={t('selectWorkspaceDescription')}>
      <div className="flex flex-col gap-2">
        {memberships.map((membership) => {
          const isCurrent = membership.tenantId === tenant?._id;
          return (
            <button
              key={membership.tenantId}
              type="button"
              onClick={() => handleSelect(membership)}
              disabled={isSwitching}
              className="flex items-center justify-between w-full px-4 py-3 text-left border rounded-lg hover:bg-accent disabled:opacity-50 disabled:pointer-events-none"
            >
              <span className="flex flex-col">
                <span className="text-sm font-medium">{membership.name}</span>
                <span className="text-xs text-muted-foreground">{membership.slug}</span>
              </span>
              {isCurrent && (
                <Check className="w-4 h-4 text-primary" aria-label={t('selectWorkspaceCurrent')} />
              )}
              {membership.status === 'pending' && (
                <Badge variant="secondary">{t('selectWorkspacePending')}</Badge>
              )}
            </button>
          );
        })}
      </div>
    </AuthPageLayout>
  );
}
